import React, { useState, useEffect } from "react";
import {
  ThinTextField,
  AddButton,
  ThinDropdown,
} from "../../../components/common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getGrantTypeConfig } from "../grantTypeConfig";

const AMOUNT_DECIMALS = 6;
const AMOUNT_EPSILON = 0.0000005;
const formatMillionAmount = (value) =>
  (Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: AMOUNT_DECIMALS,
  });

const MilestonesProgress = ({
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  showUpdateOnly,
  onStoreFormData,
  onValidationChange,
  onGrantUpdated,
  initialData,
  approvedAmountPkr,
  approvedAmountEuro,
  includeSevenPercentAdvance,
  sevenPercentAdjustMilestone,
  grantType = "sme",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cfg = getGrantTypeConfig(grantType);

  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Not every grant includes a 7% advance, so it's an opt-in toggle. It's
  // stored explicitly on the grant (include_seven_percent_advance) rather
  // than inferred from milestone rows, so it can't drift out of sync with
  // milestone numbering. When on, it's a fixed extra entry (milestone_number
  // 0) separate from the "Number of Milestones" count; its amount is 7% of
  // the approved grant amount, not something the user types in.
  const [includeSevenPercent, setIncludeSevenPercent] = useState(
    typeof includeSevenPercentAdvance === "boolean"
      ? includeSevenPercentAdvance
      : !initialData ||
          initialData.length === 0 ||
          initialData.some((m) => Number(m.milestone_number) === 0)
  );

  const calculateSevenPercent = (approvedAmount) => {
    const value = parseFloat(approvedAmount);
    return isNaN(value) ? "" : String(Number((value * 0.07).toFixed(AMOUNT_DECIMALS)));
  };

  const createAdvanceMilestone = (adjustAt = "") => ({
    id: null,
    milestone_number: 0,
    milestone_title: "7% Advance",
    adjust_at_milestone: adjustAt,
    documents_required: "",
    amount_pkr: calculateSevenPercent(approvedAmountPkr),
    amount_euro: calculateSevenPercent(approvedAmountEuro),
    amount_released_pkr: "",
    amount_released_euro: "",
    deadline_date: "",
    status: "",
    remarks: "",
  });

  const generateRealMilestones = (count) => {
    const newMilestones = [];
    for (let i = 0; i < count; i++) {
      newMilestones.push({
        id: null,
        milestone_number: i + 1,
        milestone_title: "",
        documents_required: "",
        amount_pkr: "",
        amount_euro: "",
        amount_released_pkr: "",
        amount_released_euro: "",
        deadline_date: "",
        status: "", // Empty string
        remarks: "",
      });
    }
    return newMilestones;
  };

  const [milestoneCount, setMilestoneCount] = useState(() => {
    if (!initialData || initialData.length === 0) return 1;
    const hasAdvance = initialData.some(
      (m) => Number(m.milestone_number) === 0
    );
    return hasAdvance ? initialData.length - 1 : initialData.length;
  });

  const [milestones, setMilestones] = useState(() => {
    if (initialData && initialData.length > 0) {
      // milestone_number 0 = 7% Advance, 1-5 = real milestones. Detected
      // purely by the value of milestone_number itself, never by array
      // position/sort order, so a saved record is never misread.
      const sorted = [...initialData].sort(
        (a, b) => (Number(a.milestone_number) || 0) - (Number(b.milestone_number) || 0)
      );
      return sorted.map((m) => {
        const isAdvance = Number(m.milestone_number) === 0;
        return {
          ...m,
          id: m.milestone_id || m.id || null,
          milestone_number: Number(m.milestone_number) || 0,
          milestone_title: isAdvance ? "7% Advance" : m.milestone_title || "",
          ...(isAdvance && {
            adjust_at_milestone:
              m.adjust_at_milestone ?? sevenPercentAdjustMilestone ?? "",
          }),
          amount_pkr: isAdvance
            ? calculateSevenPercent(approvedAmountPkr)
            : m.amount_pkr || "",
          amount_euro: isAdvance
            ? calculateSevenPercent(approvedAmountEuro)
            : m.amount_euro || "",
          amount_released_pkr: m.amount_released_pkr || "",
          amount_released_euro: m.amount_released_euro || "",
          status: m.status || "", // Empty string instead of default value
        };
      });
    }
    return [createAdvanceMilestone(), ...generateRealMilestones(1)];
  });

  // Keep the 7% Advance amount in sync if the approved grant amount changes
  useEffect(() => {
    setMilestones((prev) =>
      prev.map((m) =>
        m.milestone_number === 0
          ? {
              ...m,
              amount_pkr: calculateSevenPercent(approvedAmountPkr),
              amount_euro: calculateSevenPercent(approvedAmountEuro),
            }
          : m
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedAmountPkr, approvedAmountEuro]);

  const handleMilestoneCountChange = (newCount) => {
    // Clear the input first to prevent concatenation
    const count = parseInt(newCount) || 1;

    if (newCount === "" || newCount === null) {
      setMilestoneCount("");
      return;
    }

    if (count < 1 || count > 5) {
      toast.error("Milestone count must be between 1 and 5");
      setMilestoneCount(milestoneCount); // Reset to previous valid value
      return;
    }

    const previousAdjust = Number(
      milestones.find((m) => m.milestone_number === 0)?.adjust_at_milestone
    );
    const nextMilestones = includeSevenPercent
      ? [
          createAdvanceMilestone(previousAdjust >= 1 && previousAdjust <= count ? previousAdjust : ""),
          ...generateRealMilestones(count),
        ]
      : generateRealMilestones(count);

    setMilestoneCount(count);
    setMilestones(nextMilestones);

    // Reset touched and errors
    setTouched({});
    setFieldErrors({});

    toast.info("Milestone data has been reset due to count change");
  };

  // Turning the 7% Advance on/off adds or removes that one entry without
  // touching the real milestones already entered.
  const handleToggleSevenPercent = (e) => {
    const value = e.target.value === "true";
    setIncludeSevenPercent(value);
    setMilestones((prev) => {
      const realOnly = prev
        .filter((m) => m.milestone_number !== 0)
        .map((m, i) => ({ ...m, milestone_number: i + 1 }));
      return value ? [createAdvanceMilestone(), ...realOnly] : realOnly;
    });
  };

  const validateMilestonesWithErrors = (showToasts = false, forceTouched = null) => {
    const activeTouched = forceTouched || touched;
    const errors = {};
    let isValid = true;

    // Calculate total amounts
    const totalPKR = milestones.reduce((total, milestone) => {
      return total + (parseFloat(milestone.amount_pkr) || 0);
    }, 0);

    const totalEuro = milestones.reduce((total, milestone) => {
      return total + (parseFloat(milestone.amount_euro) || 0);
    }, 0);

    // Convert approvedAmount values to numbers and handle null/undefined cases
    const approvedPkr = parseFloat(approvedAmountPkr) || 0;
    const approvedEuro = parseFloat(approvedAmountEuro) || 0;

    // Check if total milestone amounts match approved grant amounts (only show toast if requested)
    if (approvedAmountPkr && Math.abs(totalPKR - approvedPkr) > AMOUNT_EPSILON) {
      if (showToasts) {
        toast.error(
          `Total Amount of milestone must be equal to Total Approved amount PKR (${formatMillionAmount(
            approvedPkr
          )}). Current total: ${formatMillionAmount(totalPKR)}`
        );
      }
      isValid = false;
    }

    if (approvedAmountEuro && Math.abs(totalEuro - approvedEuro) > AMOUNT_EPSILON) {
      if (showToasts) {
        toast.error(
          `Total Amount of milestone must be equal to Total Approved amount Euro (${formatMillionAmount(
            approvedEuro
          )}). Current total: ${formatMillionAmount(totalEuro)}`
        );
      }
      isValid = false;
    }

    for (let i = 0; i < milestones.length; i++) {
      const milestone = milestones[i];
      const milestoneErrors = {};

      // Only validate fields that have been touched
      if (
        activeTouched[`${i}_milestone_title`] &&
        (!milestone.milestone_title || !milestone.milestone_title.trim())
      ) {
        milestoneErrors.milestone_title = "Milestone title is required";
        isValid = false;
      }

      if (
        activeTouched[`${i}_documents_required`] &&
        (!milestone.documents_required || !milestone.documents_required.trim())
      ) {
        milestoneErrors.documents_required = "Documents required is required";
        isValid = false;
      }
      // Remarks validation for delayed or postponed
      if (
        (milestone.status === "Delayed" || milestone.status === "Postponed") &&
        (!milestone.remarks || !milestone.remarks.trim())
      ) {
        milestoneErrors.remarks = "Remarks is required ";
        isValid = false;
      }
      // Validate PKR amount only if touched
      if (milestone.milestone_number !== 0 && activeTouched[`${i}_amount_pkr`]) {
        if (!milestone.amount_pkr || milestone.amount_pkr.trim() === "") {
          milestoneErrors.amount_pkr = "PKR amount is required";
          isValid = false;
        } else {
          const pkrValue = parseFloat(milestone.amount_pkr);
          if (isNaN(pkrValue) || pkrValue <= 0) {
            milestoneErrors.amount_pkr = "PKR amount must be greater than 0";
            isValid = false;
          }
        }
      }

      // Validate Euro amount only if touched
      if (milestone.milestone_number !== 0 && activeTouched[`${i}_amount_euro`]) {
        if (!milestone.amount_euro || milestone.amount_euro.trim() === "") {
          milestoneErrors.amount_euro = "Euro amount is required";
          isValid = false;
        } else {
          const euroValue = parseFloat(milestone.amount_euro);
          if (isNaN(euroValue) || euroValue <= 0) {
            milestoneErrors.amount_euro = "Euro amount must be greater than 0";
            isValid = false;
          }
        }
      }

      // Amount Released cannot exceed this milestone's Amount PKR/Euro
      if (
        activeTouched[`${i}_amount_released_pkr`] &&
        milestone.amount_released_pkr !== "" &&
        parseFloat(milestone.amount_released_pkr) > (parseFloat(milestone.amount_pkr) || 0)
      ) {
        milestoneErrors.amount_released_pkr = "Amount Released PKR cannot exceed Amount PKR";
        isValid = false;
      }

      if (
        activeTouched[`${i}_amount_released_euro`] &&
        milestone.amount_released_euro !== "" &&
        parseFloat(milestone.amount_released_euro) > (parseFloat(milestone.amount_euro) || 0)
      ) {
        milestoneErrors.amount_released_euro = "Amount Released Euro cannot exceed Amount Euro";
        isValid = false;
      }

      if (Object.keys(milestoneErrors).length > 0) {
        errors[i] = milestoneErrors;
      }
    }

    setFieldErrors(errors);


    return isValid;
  };

  // Quiet validation for real-time feedback
  const validateMilestonesQuiet = () => {
    // Calculate total amounts
    const totalPKR = milestones.reduce((total, milestone) => {
      return total + (parseFloat(milestone.amount_pkr) || 0);
    }, 0);

    const totalEuro = milestones.reduce((total, milestone) => {
      return total + (parseFloat(milestone.amount_euro) || 0);
    }, 0);

    // Check if total milestone amounts match approved grant amounts
    if (approvedAmountPkr && Math.abs(totalPKR - approvedAmountPkr) > AMOUNT_EPSILON) {
      return false;
    }

    if (approvedAmountEuro && Math.abs(totalEuro - approvedAmountEuro) > AMOUNT_EPSILON) {
      return false;
    }

    for (let i = 0; i < milestones.length; i++) {
      const milestone = milestones[i];

      // The 7% Advance (if included) only shows amount fields; title/documents/deadline
      // don't apply to it.
      if (milestone.milestone_number !== 0) {
        if (!milestone.milestone_title.trim()) {
          return false;
        }

        if (
          !milestone.documents_required ||
          !milestone.documents_required.trim()
        ) {
          return false;
        }
      }

      if (
        milestone.milestone_number !== 0 &&
        (!milestone.amount_pkr || parseFloat(milestone.amount_pkr) <= 0)
      ) {
        return false;
      }

      if (
        milestone.milestone_number !== 0 &&
        (!milestone.amount_euro || parseFloat(milestone.amount_euro) <= 0)
      ) {
        return false;
      }

      if (
        milestone.amount_released_pkr !== "" &&
        parseFloat(milestone.amount_released_pkr) > (parseFloat(milestone.amount_pkr) || 0)
      ) {
        return false;
      }

      if (
        milestone.amount_released_euro !== "" &&
        parseFloat(milestone.amount_released_euro) > (parseFloat(milestone.amount_euro) || 0)
      ) {
        return false;
      }
    }
    return true;
  };

  // Validation with user feedback
  const validateMilestones = () => {
    const newTouched = {};
    milestones.forEach((milestone, index) => {
      // The 7% Advance (if included) only exposes amount fields
      if (milestone.milestone_number !== 0) {
        newTouched[`${index}_milestone_title`] = true;
        newTouched[`${index}_documents_required`] = true;
        newTouched[`${index}_remarks`] = true;
      }
      newTouched[`${index}_amount_pkr`] = true;
      newTouched[`${index}_amount_euro`] = true;
      newTouched[`${index}_amount_released_pkr`] = true;
      newTouched[`${index}_amount_released_euro`] = true;
    });
    setTouched(newTouched);

    const isValid = validateMilestonesWithErrors(true, newTouched);
    if (!isValid) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (onValidationChange) {
      const isValid = validateMilestonesQuiet();
      onValidationChange(isValid);
    }
  }, [milestones, onValidationChange, approvedAmountPkr, approvedAmountEuro]);

  useEffect(() => {
    if (onStoreFormData) {
      onStoreFormData(milestones);
    }
  }, [milestones, onStoreFormData]);

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validateMilestonesWithErrors(false);
    }
  }, [milestones, touched, approvedAmountPkr, approvedAmountEuro]);

  const handleMilestoneChange = (index, field, value) => {
    const updatedMilestones = [...milestones];

    if (
      field === "amount_pkr" ||
      field === "amount_euro" ||
      field === "amount_released_pkr" ||
      field === "amount_released_euro"
    ) {
      // Only allow numbers, capped at 6 decimal places (amounts are in millions)
      const numValue = value.replace(/[^0-9.]/g, "");
      const isValidFormat = numValue === "" || /^\d*\.?\d{0,6}$/.test(numValue);
      if (
        isValidFormat &&
        (numValue === "" ||
          (!isNaN(parseFloat(numValue)) && parseFloat(numValue) >= 0))
      ) {
        updatedMilestones[index][field] = numValue;
      }
    } else {
      updatedMilestones[index][field] = value;
    }

    setMilestones(updatedMilestones);

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [`${index}_${field}`]: true,
    }));
  };

  const handleBlur = (index, field) => {
    setTouched((prev) => ({
      ...prev,
      [`${index}_${field}`]: true,
    }));
  };

  const calculateTotalAmounts = () => {
    const totalPKR = milestones.reduce((total, milestone) => {
      return total + (parseFloat(milestone.amount_pkr) || 0);
    }, 0);

    const totalEuro = milestones.reduce((total, milestone) => {
      return total + (parseFloat(milestone.amount_euro) || 0);
    }, 0);

    return { totalPKR, totalEuro };
  };

  const saveMilestones = async () => {
    if (!validateMilestones()) {
      return false;
    }

    if (!grantId) {
      toast.success("Milestones data saved locally");
      return true;
    }

    try {
      setLoading(true);

      // Re-derive milestone_number from the current toggle + array contents
      // right before sending, instead of trusting whatever is already on
      // each milestone object, so the advance is always saved as 0 and the
      // real milestones as a clean 1..N regardless of how the toggle/count
      // inputs were used to get here.
      const realMilestones = milestones
        .filter((m) => m.milestone_number !== 0)
        .map((m, i) => ({ ...m, milestone_number: i + 1 }));
      const advanceMilestone = milestones.find((m) => m.milestone_number === 0);
      const normalizedMilestones = includeSevenPercent
        ? [advanceMilestone || createAdvanceMilestone(), ...realMilestones]
        : realMilestones;

      const milestonesData = normalizedMilestones.map((milestone) => ({
        id: milestone.id,
        milestone_number: milestone.milestone_number, // 0 = 7% Advance, 1-5 = real milestones
        milestone_title: milestone.milestone_title.trim(),
        documents_required: milestone.documents_required || null,
        amount_pkr: parseFloat(milestone.amount_pkr),
        amount_euro: parseFloat(milestone.amount_euro),
        amount_released_pkr:
          milestone.amount_released_pkr === ""
            ? null
            : parseFloat(milestone.amount_released_pkr),
        amount_released_euro:
          milestone.amount_released_euro === ""
            ? null
            : parseFloat(milestone.amount_released_euro),
        deadline_date: milestone.deadline_date || null,
        status: milestone.status || "In progress",
        remarks: milestone.remarks || null,
      }));

      const response = await fetchData(
        `${cfg.apiBase}/${grantId}/milestones/save`,
        "PUT",
        {
          milestones: milestonesData,
          include_seven_percent_advance: includeSevenPercent,
          seven_percent_adjust_milestone: includeSevenPercent
            ? Number(advanceMilestone?.adjust_at_milestone) || null
            : null,
        },
        {},
        {},
        navigate
      );

      if (
        response &&
        (response.success === true ||
          response.message === "Milestones saved successfully")
      ) {
        toast.success("Milestones saved successfully to database");

        if (response.milestones) {
          const updatedMilestones = response.milestones.map((milestone) => ({
            ...milestone,
            id: milestone.milestone_id || milestone.id,
            ...(Number(milestone.milestone_number) === 0 && {
              adjust_at_milestone: advanceMilestone?.adjust_at_milestone ?? "",
            }),
          }));
          setMilestones(updatedMilestones);
        }

        try {
          sessionStorage.removeItem("milestonesProgress");
        } catch (error) {
          console.warn("Failed to clear sessionStorage:", error);
        }

        if (onGrantUpdated) {
          onGrantUpdated();
        }
        return true;
      } else {
        console.error("Failed to save milestones:", response);
        toast.error("Failed to save milestones to database");
        return false;
      }
    } catch (error) {
      console.error("Error saving milestones:", error);
      toast.error("Error saving milestones: " + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Strict validation before proceeding
    if (!validateMilestones()) {
      return;
    }

    // ALWAYS store milestones data locally first
    if (onStoreFormData) {
      onStoreFormData(milestones);
    }

    // Store in sessionStorage as backup
    try {
      sessionStorage.setItem("milestonesProgress", JSON.stringify(milestones));
    } catch (error) {
      console.warn("Failed to store milestones in sessionStorage:", error);
    }

    if (!grantId) {
      toast.success("Milestones progress saved");

      if (onNext) {
        onNext();
      }
      return;
    }

    // If grant exists, try to save to database
    const saved = await saveMilestones();
    if (saved && onNext) {
      onNext();
    }
  };

  // Function to handle Update button (only for edit mode)
  const handleUpdate = async () => {
    if (!grantId) {
      toast.error("Grant must be created before updating milestones");
      return;
    }

    // Validate before updating
    if (!validateMilestones()) {
      return;
    }

    await saveMilestones();
  };

  const { totalPKR, totalEuro } = calculateTotalAmounts();

  const statusOptions = [
    { label: "Completed", value: "Completed" },
    { label: "In progress", value: "In progress" },
    { label: "Delayed", value: "Delayed" },
    { label: "Postponed", value: "Postponed" },
  ];

  // Helper function to get error message for a specific field
  const getFieldError = (index, field) => {
    return fieldErrors[index]?.[field];
  };

  // Helper function to check if field should show error
  const shouldShowError = (index, field) => {
    return touched[`${index}_${field}`] && getFieldError(index, field);
  };

  // Difference = Approved amount - Amount released
  const calculateDifference = (approved, released) => {
    if (released === "" || released === null || released === undefined) {
      return "";
    }
    const approvedValue = parseFloat(approved) || 0;
    const releasedValue = parseFloat(released) || 0;
    return String(Number(Math.max(0, approvedValue - releasedValue).toFixed(AMOUNT_DECIMALS)));
  };

  const MILESTONE_LABELS = [
    "7% Advance",
    "1st Milestone",
    "2nd Milestone",
    "3rd Milestone",
    "4th Milestone",
    "5th Milestone",
  ];
  const getMilestoneLabel = (milestoneNumber) =>
    MILESTONE_LABELS[milestoneNumber] || `Milestone ${milestoneNumber}`;
  const advanceIndex = milestones.findIndex((m) => m.milestone_number === 0);

  return (
    <div className="space-y-4">
      {/* Milestone setup: the count comes first because the 7% advance is adjusted in one of these milestones */}
      <div className="bg-blue-50 p-4 rounded-lg mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div>
            <ThinTextField
              label="Number of Milestones"
              value={milestoneCount}
              onChange={(e) => handleMilestoneCountChange(e.target.value)}
              type="number"
              min="1"
              max="5"
              placeholder="Enter number of milestones (1-5)"
              style={{
                MozAppearance: "textfield",
                appearance: "textfield",
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => {
                e.target.blur();
              }}
            />
          </div>
          <div>
            <ThinDropdown
              label="Include 7% Advance?"
              id="include-seven-percent-dropdown"
              options={[
                { value: true, label: "Yes" },
                { value: false, label: "No" },
              ]}
              value={includeSevenPercent}
              onChange={handleToggleSevenPercent}
              isRequired={false}
            />
          </div>
          {includeSevenPercent && advanceIndex !== -1 && (
            <div>
              <ThinDropdown
                label="7% Advance Adjusted In"
                id="seven-percent-adjust-milestone-dropdown"
                options={milestones
                  .filter((m) => m.milestone_number !== 0)
                  .map((m) => ({
                    value: m.milestone_number,
                    label: getMilestoneLabel(m.milestone_number),
                  }))}
                value={milestones[advanceIndex].adjust_at_milestone}
                onChange={(e) =>
                  handleMilestoneChange(
                    advanceIndex,
                    "adjust_at_milestone",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                isRequired={false}
              />
            </div>
          )}
        </div>
        <p className="mt-3 text-sm text-blue-800">
          Changing the milestone count will reset all milestone data.
        </p>
      </div>

      {/* Approved vs Current Amounts Display */}
      <div className="bg-gray-100 p-4 rounded-lg mb-4">
        <h3 className="font-semibold text-gray-700 mb-2">Amount Comparison</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-2">
              <span className="text-gray-600">Approved Grant Amount PKR (M): </span>
              <span className="font-bold text-green-600">
                {approvedAmountPkr
                  ? `Rs ${formatMillionAmount(approvedAmountPkr)}`
                  : "Not Set"}
              </span>
            </div>
            <div>
              <span className="text-gray-600">
                Current Milestone Total PKR (M):{" "}
              </span>
              <span
                className={`font-bold ${
                  Math.abs(totalPKR - (approvedAmountPkr || 0)) <= AMOUNT_EPSILON
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {`Rs ${formatMillionAmount(totalPKR)}`}
              </span>
            </div>
          </div>
          <div>
            <div className="mb-2">
              <span className="text-gray-600">
                Approved Grant Amount Euro (M):{" "}
              </span>
              <span className="font-bold text-blue-600">
                {approvedAmountEuro
                  ? `€${formatMillionAmount(approvedAmountEuro)}`
                  : "Not Set"}
              </span>
            </div>
            <div>
              <span className="text-gray-600">
                Current Milestone Total Euro (M):{" "}
              </span>
              <span
                className={`font-bold ${
                  Math.abs(totalEuro - (approvedAmountEuro || 0)) <= AMOUNT_EPSILON
                    ? "text-blue-600"
                    : "text-red-600"
                }`}
              >
                €
                {formatMillionAmount(totalEuro)}
              </span>
            </div>
          </div>
        </div>
        {(approvedAmountPkr && Math.abs(totalPKR - approvedAmountPkr) > AMOUNT_EPSILON) ||
        (approvedAmountEuro &&
          Math.abs(totalEuro - approvedAmountEuro) > AMOUNT_EPSILON) ? (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
            <p className="text-red-800 text-sm font-medium">
              ⚠️ Total milestone amounts must equal approved grant amounts
            </p>
          </div>
        ) : (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
            <p className="text-green-800 text-sm font-medium">
              ✓ Milestone amounts match approved grant amounts
            </p>
          </div>
        )}
      </div>

      {milestones.map((milestone, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg p-4 space-y-4"
        >
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">
              {getMilestoneLabel(milestone.milestone_number)}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {milestone.milestone_number !== 0 && (
              <>
                {/* Milestone Title */}
                <div>
                  <ThinTextField
                    label="Milestone Title (Activities)"
                    value={milestone.milestone_title}
                    onChange={(e) =>
                      handleMilestoneChange(
                        index,
                        "milestone_title",
                        e.target.value
                      )
                    }
                    onBlur={() => handleBlur(index, "milestone_title")}
                    placeholder="Enter milestone title"
                    error={shouldShowError(index, "milestone_title")}
                  />
                  {shouldShowError(index, "milestone_title") && (
                    <div className="text-red-500 text-sm mt-1">
                      {getFieldError(index, "milestone_title")}
                    </div>
                  )}
                </div>

                {/* Documents Required */}
                <div>
                  <ThinTextField
                    label="Documents Required"
                    value={milestone.documents_required}
                    onChange={(e) =>
                      handleMilestoneChange(
                        index,
                        "documents_required",
                        e.target.value
                      )
                    }
                    onBlur={() => handleBlur(index, "documents_required")}
                    placeholder="List required documents"
                    error={shouldShowError(index, "documents_required")}
                    multiline
                    rows={2}
                  />
                  {shouldShowError(index, "documents_required") && (
                    <div className="text-red-500 text-sm mt-1">
                      {getFieldError(index, "documents_required")}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Amount PKR */}
            <div>
              <ThinTextField
                label={milestone.milestone_number === 0 ? "Amount PKR (M) (7% of Approved)" : "Amount PKR (M)"}
                value={milestone.amount_pkr}
                onChange={(e) =>
                  handleMilestoneChange(index, "amount_pkr", e.target.value)
                }
                onBlur={() => handleBlur(index, "amount_pkr")}
                placeholder="e.g. 1.5 (million)"
                type="text"
                disabled={milestone.milestone_number === 0}
                error={shouldShowError(index, "amount_pkr")}
              />
              {milestone.milestone_number === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Auto-calculated as 7% of the Approved Grant Amount (PKR)
                </p>
              )}
              {shouldShowError(index, "amount_pkr") && (
                <div className="text-red-500 text-sm mt-1">
                  {getFieldError(index, "amount_pkr")}
                </div>
              )}
            </div>

            {/* Amount Euro */}
            <div>
              <ThinTextField
                label={milestone.milestone_number === 0 ? "Amount Euro (M) (7% of Approved)" : "Amount Euro (M)"}
                value={milestone.amount_euro}
                onChange={(e) =>
                  handleMilestoneChange(index, "amount_euro", e.target.value)
                }
                onBlur={() => handleBlur(index, "amount_euro")}
                placeholder="e.g. 1.5 (million)"
                type="text"
                disabled={milestone.milestone_number === 0}
                error={shouldShowError(index, "amount_euro")}
              />
              {milestone.milestone_number === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Auto-calculated as 7% of the Approved Grant Amount (Euro)
                </p>
              )}
              {shouldShowError(index, "amount_euro") && (
                <div className="text-red-500 text-sm mt-1">
                  {getFieldError(index, "amount_euro")}
                </div>
              )}
            </div>

            {/* Amount Released PKR */}
            <div>
              <ThinTextField
                label="Amount Released PKR (M)"
                value={milestone.amount_released_pkr}
                onChange={(e) =>
                  handleMilestoneChange(
                    index,
                    "amount_released_pkr",
                    e.target.value
                  )
                }
                onBlur={() => handleBlur(index, "amount_released_pkr")}
                placeholder="e.g. 1.5 (million)"
                type="text"
                isRequired={false}
                error={shouldShowError(index, "amount_released_pkr")}
              />
              {shouldShowError(index, "amount_released_pkr") && (
                <div className="text-red-500 text-sm mt-1">
                  {getFieldError(index, "amount_released_pkr")}
                </div>
              )}
            </div>

            {/* Difference PKR (computed) */}
            <div>
              <ThinTextField
                label="Difference PKR (M)"
                value={calculateDifference(
                  milestone.amount_pkr,
                  milestone.amount_released_pkr
                )}
                disabled
                isRequired={false}
                placeholder="Auto-calculated"
              />
            </div>

            {/* Amount Released Euro */}
            <div>
              <ThinTextField
                label="Amount Released Euro (M)"
                value={milestone.amount_released_euro}
                onChange={(e) =>
                  handleMilestoneChange(
                    index,
                    "amount_released_euro",
                    e.target.value
                  )
                }
                onBlur={() => handleBlur(index, "amount_released_euro")}
                placeholder="e.g. 1.5 (million)"
                type="text"
                isRequired={false}
                error={shouldShowError(index, "amount_released_euro")}
              />
              {shouldShowError(index, "amount_released_euro") && (
                <div className="text-red-500 text-sm mt-1">
                  {getFieldError(index, "amount_released_euro")}
                </div>
              )}
            </div>

            {/* Difference Euro (computed) */}
            <div>
              <ThinTextField
                label="Difference Euro (M)"
                value={calculateDifference(
                  milestone.amount_euro,
                  milestone.amount_released_euro
                )}
                disabled
                isRequired={false}
                placeholder="Auto-calculated"
              />
            </div>

            {milestone.milestone_number !== 0 && (
              <>
                {/* Deadline Date */}
                <div>
                  <ThinTextField
                    label="Date/Deadline"
                    value={milestone.deadline_date}
                    onChange={(e) =>
                      handleMilestoneChange(index, "deadline_date", e.target.value)
                    }
                    onBlur={() => handleBlur(index, "deadline_date")}
                    type="date"
                    error={shouldShowError(index, "deadline_date")}
                    isRequired={false}
                  />
                  {shouldShowError(index, "deadline_date") && (
                    <div className="text-red-500 text-sm mt-1">
                      {getFieldError(index, "deadline_date")}
                    </div>
                  )}
                </div>

                {/* Status */}
                <div>
                  <ThinDropdown
                    label="Status/Progress"
                    id={`status-dropdown-${index}`}
                    options={statusOptions}
                    value={milestone.status}
                    onChange={(e) =>
                      handleMilestoneChange(index, "status", e.target.value)
                    }
                    required={false}
                  />
                  {shouldShowError(index, "status") && (
                    <p className="text-red-500 text-xs">
                      {getFieldError(index, "status")}
                    </p>
                  )}
                </div>

                {(milestone.status === "Delayed" ||
                  milestone.status === "Postponed") && (
                  <div className="md:col-span-2">
                    <ThinTextField
                      label="Remarks"
                      value={milestone.remarks}
                      onChange={(e) =>
                        handleMilestoneChange(index, "remarks", e.target.value)
                      }
                      onBlur={() => handleBlur(index, "remarks")}
                      placeholder={`Enter remarks for ${milestone.status} status...`}
                      multiline
                      rows={3}
                      error={shouldShowError(index, "remarks")}
                    />
                    {shouldShowError(index, "remarks") && (
                      <div className="text-red-500 text-sm mt-1">
                        {getFieldError(index, "remarks")}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}

      {/* Buttons */}
      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNext}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Saving..." : "Next"}
          </button>
        )}
        {(isEditMode || showUpdateOnly) && (
          <AddButton
            text={loading ? "Updating..." : "Update"}
            type="button"
            onClick={handleUpdate}
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
};

export default MilestonesProgress;
